import React, { useState } from 'react';

/**
 * AccessTree — the customer ▸ site ▸ device FINDER (rights-acl design §8): a grouped accordion for
 * picking which devices a user can access. It is a FINDER, not the grant level — checking a device is
 * what creates/removes a grant; the per-device rights + cameras are edited elsewhere (the per-device
 * editor). Customer + site are device DESCRIPTORS here, used only to organize the browse — access is
 * per device. Multi-select: `isChecked(sDeviceID)` reflects current membership, `onToggleDevice(id)`
 * flips it. Mobile-first, className-styled under `.setup-container`.
 */

// A device's display label — its friendly name, else its serial.
export const devLabel = (d) => (d && d.sName && String(d.sName).trim()) || (d && d.sDeviceID) || '(device)';

/**
 * deviceFinderGroups — group the org's DEVICES under customer ▸ site. Built off the `devices` list
 * (authoritative for what exists — every device row carries its own sCustomerName / sProjectName /
 * bCustomerID / bCustomerProjectID), so no device is ever dropped by a missing customer/site join.
 * `customers` is used ONLY to disambiguate duplicate customer names by domain (real web1 data has two
 * "Akiva"s). Deterministically sorted (customer, then site, then device label) so the finder is
 * stable. Pure — unit-tested in AccessTree.test.js.
 * @returns {Array<{key:string,label:string,sites:Array<{key:string,label:string,devices:Array}>}>}
 */
export const deviceFinderGroups = (devices, customers) => {
  const custById = {};
  (customers || []).forEach((c) => { custById[c.bID] = c; });
  const nameCounts = {};
  (customers || []).forEach((c) => {
    const k = (c.sName || '').trim().toLowerCase();
    if (k) nameCounts[k] = (nameCounts[k] || 0) + 1;
  });

  const byCust = new Map();
  (devices || []).forEach((d) => {
    if (!d || !d.sDeviceID) return;
    const cid = d.bCustomerID != null ? d.bCustomerID : '_none';
    if (!byCust.has(cid)) byCust.set(cid, []);
    byCust.get(cid).push(d);
  });

  const groups = [];
  byCust.forEach((devs, cid) => {
    const c = custById[cid];
    const cname = (devs[0] && devs[0].sCustomerName) || (c && c.sName) || 'No customer';
    const k = (cname || '').trim().toLowerCase();
    const domain = c && c.sDomain;
    const label = nameCounts[k] > 1 && domain ? `${cname} (${domain})` : cname;

    const bySite = new Map();
    devs.forEach((d) => {
      const pid = d.bCustomerProjectID != null ? d.bCustomerProjectID : '_none';
      if (!bySite.has(pid)) bySite.set(pid, []);
      bySite.get(pid).push(d);
    });
    const sites = [];
    bySite.forEach((sdevs, pid) => {
      sites.push({
        key: String(cid) + '/' + String(pid),
        label: (sdevs[0] && sdevs[0].sProjectName) || 'No site',
        devices: sdevs.slice().sort((a, b) => devLabel(a).localeCompare(devLabel(b))),
      });
    });
    sites.sort((a, b) => a.label.localeCompare(b.label));
    groups.push({ key: String(cid), label, sites });
  });
  groups.sort((a, b) => a.label.localeCompare(b.label));
  return groups;
};

export const AccessTree = ({ devices, customers, isChecked, onToggleDevice }) => {
  const groups = deviceFinderGroups(devices, customers);
  const [openC, setOpenC] = useState(() => new Set());
  const [openS, setOpenS] = useState(() => new Set());
  const onlyCust = groups.length === 1 ? groups[0].key : null;   // single customer → auto-open
  const toggleC = (key) => setOpenC((p) => { const n = new Set(p); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const toggleS = (key) => setOpenS((p) => { const n = new Set(p); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  if (!groups.length) return <div className="setup-muted">No devices available.</div>;

  return (
    <div className="setup-tree">
      {groups.map((g) => {
        const cOpen = g.key === onlyCust || openC.has(g.key);
        return (
          <div key={g.key} className="setup-tree__cust">
            <div className="setup-tree__hdr" role="button" aria-expanded={cOpen} onClick={() => toggleC(g.key)}>
              <span className="setup-tree__chevron">{cOpen ? '▼' : '▶'}</span>
              <span className="setup-tree__name">{g.label}</span>
            </div>
            {cOpen ? g.sites.map((s) => {
              const sOpen = g.sites.length === 1 || openS.has(s.key);
              return (
                <div key={s.key} className="setup-tree__site">
                  <div className="setup-tree__hdr setup-tree__hdr--site" role="button" aria-expanded={sOpen} onClick={() => toggleS(s.key)}>
                    <span className="setup-tree__chevron">{sOpen ? '▼' : '▶'}</span>
                    <span className="setup-tree__name">{s.label}</span>
                  </div>
                  {sOpen ? s.devices.map((d) => (
                    <label key={d.sDeviceID} className="setup-tree__device">
                      <input type="checkbox" checked={!!isChecked(d.sDeviceID)} onChange={() => onToggleDevice(d.sDeviceID)} />
                      <span className="setup-tree__device-name">{devLabel(d)}</span>
                      {d.fOnline ? null : <span className="setup-tree__offline">offline</span>}
                    </label>
                  )) : null}
                </div>
              );
            }) : null}
          </div>
        );
      })}
    </div>
  );
};

export default AccessTree;
